import { useMutation, useQueryClient } from 'react-query';
import { Ban } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import toast from 'react-hot-toast';

interface Props {
  userId: string;
  username?: string;
  onBlocked?: () => void;
  className?: string;
}

export default function BlockUserButton({ userId, username, onBlocked, className = '' }: Props) {
  const queryClient = useQueryClient();

  const { mutate: blockUser, isLoading } = useMutation(
    () => apiClient.post('/users/block', { blocked_user_id: userId }),
    {
      onSuccess: () => {
        toast.success(username ? `${username} has been blocked` : 'User blocked');
        queryClient.invalidateQueries(['blocked-users']);
        onBlocked?.();
      },
      onError: (err: any) => {
        toast.error(err.response?.data?.error?.message || 'Failed to block user');
      },
    }
  );

  const handleBlock = () => {
    const name = username ? `@${username}` : 'this user';
    if (window.confirm(`Block ${name}? They will not be able to contact you.`)) {
      blockUser();
    }
  };

  return (
    <button
      onClick={handleBlock}
      disabled={isLoading}
      className={`inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50 ${className}`}
      aria-label={`Block ${username || 'user'}`}
    >
      <Ban className="h-3.5 w-3.5" />
      {isLoading ? 'Blocking...' : 'Block User'}
    </button>
  );
}
